'use client'

import * as React from 'react'
import * as CheckboxPrimitive from '@radix-ui/react-checkbox'

import { cn } from '../../../lib/utils'
import { Icon } from '..'
import s from '../../styles/animations.module.css'
import MetafieldLabel from '../MetafieldLabel'
import { FieldChange } from '../FieldChange'

const CheckboxComponent = React.forwardRef<
  React.ElementRef<typeof CheckboxPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root> & {
    indeterminate?: boolean
  }
>(({ className, indeterminate, ...props }, ref) => (
  <div className="relative inline-flex">
    <CheckboxPrimitive.Root
      ref={ref}
      className={cn(
        'peer h-4 w-4 shrink-0 rounded-sm border border-primary shadow focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50',
        indeterminate
          ? 'bg-primary text-primary-foreground'
          : 'data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground',
        className,
      )}
      {...props}
    >
      <CheckboxPrimitive.Indicator
        className={cn('flex items-center justify-center text-current')}
        forceMount={indeterminate ? true : undefined}
      >
        <Icon name={indeterminate ? 'Minus' : 'Check'} size={10} />
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  </div>
))
CheckboxComponent.displayName = CheckboxPrimitive.Root.displayName

export type CheckboxProps = {
  label: string
  value?: boolean
  originalValue?: boolean
  handleChange?: (v: boolean) => void | Promise<void>
  isDisabled?: boolean
  isSkeleton?: boolean
  isLoading?: boolean
  helpText?: string
  errorMessage?: string
  metafield?: {
    label: string
    href: string
  }
  className?: string
} & React.ComponentPropsWithoutRef<typeof CheckboxComponent>

const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      label,
      value,
      originalValue,
      className,
      handleChange,
      isDisabled,
      isSkeleton,
      isLoading,
      helpText,
      errorMessage,
      metafield,
      ...props
    },
    ref,
  ) => {
    if (isSkeleton) {
      return (
        <div className="items-top flex space-x-2 mt-2">
          <div className="peer h-4 w-4 bg-secondary rounded animate-pulse" />
          <div className="grid gap-1.5 leading-none">
            <div className="peer h-3 w-16 bg-secondary rounded-xl animate-pulse" />
            {helpText && (
              <div className="peer h-3 w-32 bg-secondary rounded-xl animate-pulse" />
            )}
          </div>
        </div>
      )
    }

    // Check if we should show FieldChange component
    const shouldShowFieldChange =
      originalValue !== undefined && originalValue !== props.checked

    return (
      <div className={cn('flex items-start space-x-2', className)}>
        <CheckboxComponent
          title={`checkbox-${props.id}`}
          disabled={isDisabled}
          checked={props.checked}
          onCheckedChange={handleChange}
          id={props.id}
          className="mt-0.5"
        />
        <div className="grid gap-1.5 leading-none">
          <div className="flex gap-2 items-center min-h-[20px]">
            <label
              htmlFor={props.id}
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 flex gap-2"
            >
              {label}
              {metafield && <MetafieldLabel align="left" {...metafield} />}
            </label>
            {shouldShowFieldChange && (
              <FieldChange
                oldValue={originalValue ? 'Yes' : 'No'}
                newValue={props.checked ? 'Yes' : 'No'}
                size="sm"
                handleRevert={() => {
                  if (handleChange) {
                    handleChange(originalValue || false)
                  }
                }}
              />
            )}
          </div>
          <p className="text-xs text-muted-foreground">{helpText}</p>
          {errorMessage && (
            <div
              className={cn(
                'text-destructive text-xs mt-2',
                s.smallTransitionIn,
              )}
            >
              {errorMessage}
            </div>
          )}
        </div>
      </div>
    )
  },
)

Checkbox.displayName = 'Checkbox'

export { CheckboxComponent, Checkbox }
